import { Link } from "react-router-dom";
import { Wine, ArrowLeft, Heart, Skull, ArrowRight, Check, Share2 } from "lucide-react";

const BACKER_TIERS = [
  { amount: "$5", label: "Case Watcher", perks: ["A thank-you in the credits of Case #009", "Updates on every case drop before launch"] },
  { amount: "$25", label: "Case Insider", perks: ["Everything above", "A free consumer kit of your choice ($35 value)", "Early playtest access to Case #009 before launch"] },
  { amount: "$100", label: "Case Patron", perks: ["Everything above", "A character named after you (or your friend) in Case #009", "A signed NightBlood novel"] },
  { amount: "$500", label: "Case Founder", perks: ["Everything above", "A private, personalized mystery written for your friend group (one-time, up to 8 players)", "Permanent credit as a Founding Patron on wasntme.party"] },
];

const MILESTONES = [
  { goal: "$2,500", title: "Case #009 — The Vineyard Vanishing", desc: "A late-summer winery murder, 6-10 players. Playtested, illustrated, and shipped." },
  { goal: "$5,000", title: "Audio Pack for every case", desc: "Ghost voice clips, sound cues, and intro-music licenses for all consumer cases." },
  { goal: "$10,000", title: "The Narrator mode", desc: "An optional AI-free voice-over companion that reads the host script aloud so even the host can play." },
  { goal: "$25,000", title: "The NightBlood Case Vault, Volume I (hardcover)", desc: "A collector hardcover of ten complete cases. Shipped to every $100+ patron." },
];

const Crowdfund = () => (
  <div className="grain min-h-screen relative" data-testid="crowdfund-page">
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#0a0908]/80 border-b border-[#2d2424]">
      <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display text-xl tracking-wide">
          <Wine size={18} className="text-[#8b0000]" />
          <span>wasntme<span className="text-[#8b0000]">.party</span></span>
        </Link>
        <Link to="/" className="btn-ghost text-sm"><ArrowLeft size={14} /> Back</Link>
      </div>
    </nav>

    <section className="pt-32 pb-12 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#8b0000]/15 via-transparent to-transparent pointer-events-none" />
      <div className="relative max-w-4xl mx-auto px-6">
        <div className="font-hand text-[#d4af37] text-2xl rotate-[-2deg] mb-3">join the crowdfund</div>
        <h1 className="font-display text-5xl sm:text-6xl font-bold leading-[0.95] tracking-tight">
          Help us keep writing <span className="text-[#8b0000]">cases by hand</span>.
        </h1>
        <p className="mt-6 text-lg text-[#a39797] max-w-2xl leading-relaxed">
          Every clue, red herring, and twist on wasntme.party is written by a NightBlood Chronicles novelist — not by an AI. The crowdfund lets us keep it that way, write bigger cases, and ship audio, hardcovers, and the stuff that doesn't fit a $35 kit.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <a href="https://gofund.me/70f3afe1f" target="_blank" rel="noopener noreferrer"
            className="btn-primary" data-testid="crowdfund-main-cta">
            <Heart size={18} /> Chip in on GoFundMe
          </a>
          <Link to="/" className="btn-ghost text-sm">
            <Skull size={14} /> Prefer to buy a case?
          </Link>
        </div>
      </div>
    </section>

    <section className="py-16 border-t border-[#2d2424]" data-testid="story-section">
      <div className="max-w-3xl mx-auto px-6">
        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-3">the story</div>
        <h2 className="font-display text-4xl mb-6">A dark-fiction writer walked into a dinner party.</h2>
        <div className="space-y-4 text-[#f4f0ec] leading-relaxed">
          <p>
            NightBlood Chronicles started as a gothic-suspense novel series. The first book, <em>Awakening</em>, is on Amazon. The second is in draft. The third is a dinner at a table full of people who thought they knew each other.
          </p>
          <p>
            Somewhere between writing the fourth draft and testing a plot twist on friends over wine, the author realized the twist worked BECAUSE the friends played along — because the friends got cast as suspects, read cards, and discovered the ending together. That's how wasntme.party was born.
          </p>
          <p>
            Every case on this site is written by hand with the same care as a novel chapter. We don't fill names into a generic template — we write the whole story, then substitute only the names, signature lines, and harmless secrets your friends volunteered about themselves. Nobody writes a secret about anyone else. That's by design.
          </p>
          <p>
            Writing cases takes weeks. Playtesting takes weekends. We'd like to do more of both. That's what the crowdfund is for.
          </p>
        </div>
      </div>
    </section>

    <section className="py-16 border-t border-[#2d2424]">
      <div className="max-w-5xl mx-auto px-6">
        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-3">what backers get</div>
        <h2 className="font-display text-4xl mb-10">Pick your tier.</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="backer-tiers">
          {BACKER_TIERS.map((t, i) => (
            <div key={t.amount} className="card-noir p-6 relative overflow-hidden flex flex-col" data-testid={`tier-${i}`}>
              <div className="absolute inset-0 bg-gradient-to-br from-[#d4af37]/10 to-transparent pointer-events-none" />
              <div className="relative flex-1 flex flex-col">
                <div className="font-display text-3xl text-[#f4f0ec] mb-1">{t.amount}</div>
                <div className="font-hand text-[#d4af37] rotate-[-1deg] text-base mb-4">{t.label}</div>
                <ul className="space-y-2 text-sm text-[#a39797] flex-1">
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-start gap-2">
                      <Check size={14} className="text-[#d4af37] mt-0.5 flex-shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 text-xs text-[#a39797] italic">
          Backer perks are delivered via your GoFundMe-registered email after the campaign closes. Perks are not available for purchase outside of a backer pledge.
        </div>
      </div>
    </section>

    <section className="py-16 border-t border-[#2d2424]">
      <div className="max-w-4xl mx-auto px-6">
        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-3">stretch goals</div>
        <h2 className="font-display text-4xl mb-10">What we unlock together.</h2>
        <div className="space-y-4" data-testid="milestones">
          {MILESTONES.map((m, i) => (
            <div key={m.goal} className="card-noir p-6 flex items-start gap-5" data-testid={`milestone-${i}`}>
              <div className="font-display text-3xl text-[#8b0000] flex-shrink-0 min-w-[120px]">{m.goal}</div>
              <div>
                <div className="font-display text-xl text-[#f4f0ec] mb-1">{m.title}</div>
                <div className="text-sm text-[#a39797] leading-relaxed">{m.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 border-t border-[#2d2424]">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-3">in</div>
        <h2 className="font-display text-4xl mb-6">Chip in, share, or both.</h2>
        <p className="text-[#a39797] mb-8">Every dollar funds a new case. Every share brings a new backer. Both help.</p>
        <div className="flex flex-wrap gap-4 justify-center">
          <a href="https://gofund.me/70f3afe1f" target="_blank" rel="noopener noreferrer"
            className="btn-primary" data-testid="crowdfund-footer-cta">
            <Heart size={18} /> Back us on GoFundMe <ArrowRight size={14} />
          </a>
          <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("A dark-fiction writer is hosting your friends' murders. Every case on wasntme.party is written by hand. Help us keep it that way.")}&url=${encodeURIComponent("https://gofund.me/70f3afe1f")}`}
            target="_blank" rel="noopener noreferrer"
            className="btn-ghost text-sm" data-testid="crowdfund-share">
            <Share2 size={14} /> Share the campaign
          </a>
        </div>
      </div>
    </section>

    <footer className="border-t border-[#2d2424] py-10 text-center text-sm text-[#a39797]">
      <div className="font-display text-2xl text-[#f4f0ec]">wasntme<span className="text-[#8b0000]">.party</span></div>
      <div className="mt-1 text-xs tracking-[0.2em] text-[#d4af37]">A NIGHTBLOOD CHRONICLES PRODUCTION</div>
      <div className="mt-4 flex flex-wrap gap-4 justify-center">
        <Link to="/terms" className="hover:text-[#f4f0ec]">Terms</Link>
        <Link to="/privacy" className="hover:text-[#f4f0ec]">Privacy</Link>
        <Link to="/refunds" className="hover:text-[#f4f0ec]">Refunds</Link>
      </div>
    </footer>
  </div>
);

export default Crowdfund;
